import {
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
/* Button and Alert are gone: both render only Bootstrap class names
   (btn/btn-outline-secondary, alert/alert-warning) and this project ships
   no Bootstrap stylesheet, so they drew nothing. The page kit's
   .pg-icon-btn and .pg-notice are used instead. */
import { Form, FormCheck } from "react-bootstrap";
import TextInput from "../../components/common/FormFields/TextInput.jsx";
import TextAreaInput from "../../components/common/FormFields/TextareaInput.jsx";
import UniqueAsyncSelect from "../common/UniqueAsyncSelect.jsx";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import {
  createEvent,
  updateEvent,
  getEventById,
} from "../../../services/EventService.js";
import {
  getBatchesByTrainers,
} from "../../../services/BatchService.js";
import { getTrainers } from "../../../services/TrainerService.js";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from '../common/Toast/toast';
import {
  INVALID_SCHEDULE_MESSAGE,
  isPastSchedule,
  PAST_SCHEDULE_MESSAGE,
  toEventDateTime,
} from "../../../utils/eventSchedule";
/* Grid/Cell replace react-bootstrap's Row/Col, which render .row and
   .col-md-* -- Bootstrap's 12-column grid, deleted with the rest of
   Bootstrap. Without it every field on this form stacked full width
   instead of sitting two or three to a row. */
import { Grid, Cell } from "../Trainer/ui/Layout";
/* The page kit (pg-*) and the Grid/Cell classes both live in the trainer
   area's single stylesheet. It reached this page only because some other
   route in the bundle imported it; naming it here is what actually
   guarantees it. */
import "../Trainer/TrainerDashboard.css";
import "./EventForm.css";
import { useAuth } from "../../../context/AuthContext";
import { getClassrooms } from "../../../services/ClassroomService.js";
import DateTimePicker from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { getMe } from "../../../services/AuthService.js";
import moment from "moment";
import { RefreshCw } from "lucide-react";
import {
  PRIORITY_NOT_SET,
  getPriorityMeta,
  normalizePriority,
  priorityOptions,
} from "../../../utils/eventPriority.js";

// ─── Constants ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;
const SELECT_ALL_TRAINER_SIZE = 200;

// ─── API response helpers ─────────────────────────────────────────────────────
const extractBatchList = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp?.data?.configuedBatches)) return resp.data.configuedBatches;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.configuedBatches)) return resp.configuedBatches;
  return [];
};

const extractPagination = (resp) => {
  if (!resp) return null;
  return resp?.data?.pagination || resp?.pagination || null;
};

// ─────────────────────────────────────────────────────────────────────────────
function AddEvent() {
  const { id } = useParams();
  const [eventData, setEventData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [trainersWithoutBatches, setTrainersWithoutBatches] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isTrainer, setIsTrainer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentTrainerId, setCurrentTrainerId] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [formKey, setFormKey] = useState("init");
  const preSeededClassroomRef = useRef(null);

  // ── Trainer dropdown state ────────────────────────────────────────────────
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [trainerLoading, setTrainerLoading] = useState(false);
  const [trainerSearchLoading, setTrainerSearchLoading] = useState(false);
  const allTrainersLoadedRef = useRef(false);

  // Refs to avoid stale closures in scroll handlers
  const trainerPageRef = useRef(1);
  const trainerHasMoreRef = useRef(true);
  const trainerSearchRef = useRef("");
  const trainerLoadingRef = useRef(false);
  const trainerSearchPageRef = useRef(1);
  const trainerSearchHasMoreRef = useRef(true);
  const trainerCurrentSearchRef = useRef("");

  // ── Batch dropdown state ──────────────────────────────────────────────────
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchSearchLoading, setBatchSearchLoading] = useState(false);

  // Refs for batch pagination
  const batchPageRef = useRef(1);
  const batchHasMoreRef = useRef(true);
  const batchSearchRef = useRef("");
  const batchLoadingRef = useRef(false);
  const batchSearchPageRef = useRef(1);
  const batchSearchHasMoreRef = useRef(true);
  const batchCurrentSearchRef = useRef("");

  // ── Select-All Batch state ────────────────────────────────────────────────
  const [selectAllBatchLoading, setSelectAllBatchLoading] = useState(false);
  const [selectAllBatchDone, setSelectAllBatchDone] = useState(false);
  const [selectAllBatchTotal, setSelectAllBatchTotal] = useState(0);
  const selectAllBatchLoadingRef = useRef(false);

  // ── Classroom dropdown state ──────────────────────────────────────────────
  const [classroomOptions, setClassroomOptions] = useState([]);
  const [classroomLoading, setClassroomLoading] = useState(false);
  const [classroomSearchLoading, setClassroomSearchLoading] = useState(false);

  // Refs for classroom pagination
  const classroomPageRef = useRef(1);
  const classroomHasMoreRef = useRef(true);
  const classroomSearchRef = useRef("");
  const classroomLoadingRef = useRef(false);
  const classroomSearchPageRef = useRef(1);
  const classroomSearchHasMoreRef = useRef(true);
  const classroomCurrentSearchRef = useRef("");

  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const hasLoaded = useRef(false);
  const trainerSeq = useRef(0);
  const batchSeq = useRef(0);
  const classroomSeq = useRef(0);
  const trainerIdsRef = useRef([]);
  const batchLabelCacheRef = useRef(new Map());
  const { hasPermission } = useAuth();
  const DEFAULT_ISO = new Date().toISOString();

  // ── Permission check ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasPermission("list-event-form", "add", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to add events.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getCurrentUserIdFromData = (userData) => {
    if (!userData) return null;
    return userData.id || userData._id || userData.userId;
  };

  const getCurrentUserId = () => getCurrentUserIdFromData(currentUser);

  const mergePaginatedOptions = (prev, next) => {
    const map = new Map();
    (prev || []).forEach((o) => map.set(String(o?.value), o));
    (next || []).forEach((o) => map.set(String(o?.value), o));
    return Array.from(map.values());
  };

  const resolveBatchValue = (ids) => {
    const list = Array.isArray(ids) ? ids : [];
    return list
      .map((id) => String(id).trim())
      .filter(Boolean)
      .map((id) => ({
        value: id,
        label: batchLabelCacheRef.current.get(id) || id,
      }));
  };

  const resetSelectAllBatchState = useCallback(() => {
    setSelectAllBatchLoading(false);
    selectAllBatchLoadingRef.current = false;
    setSelectAllBatchDone(false);
    setSelectAllBatchTotal(0);
  }, []);

  // ── TRAINER PAGINATION ─────────────────────────────────────────────────────
  const fetchTrainerPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (trainerLoadingRef.current) return [];
      const seq = (trainerSeq.current += 1);
      trainerLoadingRef.current = true;
      setTrainerLoading(true);
      try {
        const resp = await getTrainers(PAGE_SIZE, page, search, false);
        if (seq !== trainerSeq.current) return [];
        const list = (resp?.data || []).map((t) => ({
          label: t.trainerName || `Trainer ${(t.id || "").slice(0, 6)}`,
          value: t.id,
          specialization: t.specialization,
          email: t.traineremail || "",
        }));
        const hasNext = Boolean(resp?.pagination?.hasNext);

        if (append) {
          setTrainerOptions((prev) => mergePaginatedOptions(prev, list));
        } else {
          // Merge with existing to preserve pre-seeded edit options
          setTrainerOptions((prev) => {
            const preSeeded = prev.filter((o) => o._preSeeded);
            return mergePaginatedOptions(preSeeded, list);
          });
        }

        trainerPageRef.current = page;
        trainerSearchRef.current = search;
        trainerHasMoreRef.current = hasNext;
        return list;
      } catch (err) {
        if (err.response?.status !== 401)
          console.error("Error loading trainers:", err);
        if (!append) setTrainerOptions([]);
        trainerHasMoreRef.current = false;
        return [];
      } finally {
        if (seq === trainerSeq.current) {
          trainerLoadingRef.current = false;
          setTrainerLoading(false);
        }
      }
    },
    [],
  );

  const fetchTrainerSearchPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (trainerLoadingRef.current) return [];
      const seq = (trainerSeq.current += 1);
      trainerLoadingRef.current = true;
      setTrainerSearchLoading(true);
      try {
        const resp = await getTrainers(PAGE_SIZE, page, search, false);
        if (seq !== trainerSeq.current) return [];
        const list = (resp?.data || []).map((t) => ({
          label: t.trainerName || `Trainer ${(t.id || "").slice(0, 6)}`,
          value: t.id,
          specialization: t.specialization,
          email: t.traineremail || "",
        }));
        const hasNext = Boolean(resp?.pagination?.hasNext);

        if (append) {
          setTrainerOptions((prev) => mergePaginatedOptions(prev, list));
        } else {
          setTrainerOptions((prev) => {
            const preSeeded = prev.filter((o) => o._preSeeded);
            return mergePaginatedOptions(preSeeded, list);
          });
        }

        trainerSearchPageRef.current = page;
        trainerSearchHasMoreRef.current = hasNext;
        return list;
      } catch (err) {
        console.error("Error loading search trainers:", err);
        if (!append) setTrainerOptions([]);
        return [];
      } finally {
        if (seq === trainerSeq.current) {
          trainerLoadingRef.current = false;
          setTrainerSearchLoading(false);
        }
      }
    },
    [],
  );

  const loadTrainerOptions = useCallback(
    async (inputValue, page = 1) => {
      if (allTrainersLoadedRef.current && !inputValue) return trainerOptions;

      if (inputValue) {
        trainerCurrentSearchRef.current = inputValue;
        trainerSearchPageRef.current = page;
        return await fetchTrainerSearchPage({
          page: page,
          search: String(inputValue || ""),
          append: page > 1,
        });
      } else {
        trainerPageRef.current = page;
        return await fetchTrainerPage({
          page: page,
          search: "",
          append: page > 1,
        });
      }
    },
    [fetchTrainerPage, fetchTrainerSearchPage, trainerOptions],
  );

  const handleTrainerMenuOpen = useCallback(async () => {
    if (allTrainersLoadedRef.current) return;
    if (trainerLoadingRef.current) return;
    await fetchTrainerPage({ page: 1, search: "", append: false });
  }, [fetchTrainerPage]);

  const handleTrainerScrollToBottom = useCallback(async () => {
    if (allTrainersLoadedRef.current) return;
    if (trainerLoadingRef.current || !trainerHasMoreRef.current) return;
    await fetchTrainerPage({
      page: trainerPageRef.current + 1,
      search: trainerSearchRef.current,
      append: true,
    });
  }, [fetchTrainerPage]);

  const handleTrainerSearchScrollToBottom = useCallback(async (searchTerm) => {
    if (trainerLoadingRef.current || !trainerSearchHasMoreRef.current) return;
    await fetchTrainerSearchPage({
      page: trainerSearchPageRef.current + 1,
      search: searchTerm,
      append: true,
    });
  }, [fetchTrainerSearchPage]);

  const refreshTrainers = useCallback(async () => {
    allTrainersLoadedRef.current = false;
    trainerSeq.current += 1;
    trainerSearchRef.current = "";
    trainerPageRef.current = 1;
    trainerHasMoreRef.current = true;
    trainerSearchPageRef.current = 1;
    trainerSearchHasMoreRef.current = true;
    trainerCurrentSearchRef.current = "";
    await fetchTrainerPage({ page: 1, search: "", append: false });
  }, [fetchTrainerPage]);

  // ── BATCH PAGINATION ───────────────────────────────────────────────────────
  const fetchBatchPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (batchLoadingRef.current) return [];
      const seq = (batchSeq.current += 1);
      batchLoadingRef.current = true;
      setBatchLoading(true);
      try {
        const trainerIds = Array.isArray(trainerIdsRef.current)
          ? trainerIdsRef.current.map((x) => String(x).trim()).filter(Boolean)
          : [];

        if (!trainerIds.length) {
          if (seq !== batchSeq.current) return [];
          setFilteredBatches([]);
          batchPageRef.current = 1;
          batchSearchRef.current = search;
          batchHasMoreRef.current = false;
          return [];
        }

        const resp = await getBatchesByTrainers(
          trainerIds,
          PAGE_SIZE,
          page,
          search,
          "name",
        );
        if (seq !== batchSeq.current) return [];

        const rawList = extractBatchList(resp);
        const pagination = extractPagination(resp);

        const list = rawList.map((b) => ({
          value: String(b.id),
          label: b.name,
          trainerId: b.trainerId || null,
        }));
        list.forEach((o) =>
          batchLabelCacheRef.current.set(
            String(o.value),
            String(o.label || o.value),
          ),
        );

        const hasNext =
          Boolean(pagination?.hasNext) || Boolean(pagination?.hasNextPage);

        if (append) {
          setFilteredBatches((prev) => mergePaginatedOptions(prev, list));
        } else {
          // Preserve pre-seeded batch options from edit load
          setFilteredBatches((prev) => {
            const preSeeded = prev.filter((o) => o._preSeeded);
            return mergePaginatedOptions(preSeeded, list);
          });
        }

        batchPageRef.current = page;
        batchSearchRef.current = search;
        batchHasMoreRef.current = hasNext;

        return list;
      } catch (err) {
        console.error("Error loading batches:", err);
        if (!append) setFilteredBatches([]);
        batchHasMoreRef.current = false;
        return [];
      } finally {
        if (seq === batchSeq.current) {
          batchLoadingRef.current = false;
          setBatchLoading(false);
        }
      }
    },
    [],
  );

  const fetchBatchSearchPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (batchLoadingRef.current) return [];
      const seq = (batchSeq.current += 1);
      batchLoadingRef.current = true;
      setBatchSearchLoading(true);
      try {
        const trainerIds = Array.isArray(trainerIdsRef.current)
          ? trainerIdsRef.current.map((x) => String(x).trim()).filter(Boolean)
          : [];

        if (!trainerIds.length) {
          if (seq !== batchSeq.current) return [];
          setFilteredBatches([]);
          return [];
        }

        const resp = await getBatchesByTrainers(
          trainerIds,
          PAGE_SIZE,
          page,
          search,
          "name",
        );
        if (seq !== batchSeq.current) return [];

        const rawList = extractBatchList(resp);
        const pagination = extractPagination(resp);

        const list = rawList.map((b) => ({
          value: String(b.id),
          label: b.name,
          trainerId: b.trainerId || null,
        }));
        list.forEach((o) =>
          batchLabelCacheRef.current.set(
            String(o.value),
            String(o.label || o.value),
          ),
        );

        const hasNext =
          Boolean(pagination?.hasNext) || Boolean(pagination?.hasNextPage);

        if (append) {
          setFilteredBatches((prev) => mergePaginatedOptions(prev, list));
        } else {
          setFilteredBatches((prev) => {
            const preSeeded = prev.filter((o) => o._preSeeded);
            return mergePaginatedOptions(preSeeded, list);
          });
        }

        batchSearchPageRef.current = page;
        batchSearchHasMoreRef.current = hasNext;

        return list;
      } catch (err) {
        console.error("Error loading search batches:", err);
        if (!append) setFilteredBatches([]);
        return [];
      } finally {
        if (seq === batchSeq.current) {
          batchLoadingRef.current = false;
          setBatchSearchLoading(false);
        }
      }
    },
    [],
  );

  const loadBatchOptions = useCallback(
    async (inputValue, page = 1) => {
      if (selectAllBatchLoadingRef.current) return filteredBatches;

      if (inputValue) {
        batchCurrentSearchRef.current = inputValue;
        batchSearchPageRef.current = page;
        return await fetchBatchSearchPage({
          page: page,
          search: String(inputValue || ""),
          append: page > 1,
        });
      } else {
        batchPageRef.current = page;
        return await fetchBatchPage({
          page: page,
          search: "",
          append: page > 1,
        });
      }
    },
    [fetchBatchPage, fetchBatchSearchPage, filteredBatches],
  );

  const handleBatchMenuOpen = useCallback(async () => {
    if (batchLoadingRef.current) return;
    if (selectAllBatchLoadingRef.current) return;
    await fetchBatchPage({ page: 1, search: "", append: false });
  }, [fetchBatchPage]);

  const handleBatchScrollToBottom = useCallback(async () => {
    if (selectAllBatchLoadingRef.current) return;
    if (batchLoadingRef.current || !batchHasMoreRef.current) return;
    await fetchBatchPage({
      page: batchPageRef.current + 1,
      search: batchSearchRef.current,
      append: true,
    });
  }, [fetchBatchPage]);

  const handleBatchSearchScrollToBottom = useCallback(async (searchTerm) => {
    if (batchLoadingRef.current || !batchSearchHasMoreRef.current) return;
    await fetchBatchSearchPage({
      page: batchSearchPageRef.current + 1,
      search: searchTerm,
      append: true,
    });
  }, [fetchBatchSearchPage]);

  const refreshBatches = useCallback(async () => {
    batchSeq.current += 1;
    batchSearchRef.current = "";
    batchPageRef.current = 1;
    batchHasMoreRef.current = true;
    batchSearchPageRef.current = 1;
    batchSearchHasMoreRef.current = true;
    batchCurrentSearchRef.current = "";
    resetSelectAllBatchState();
    await fetchBatchPage({ page: 1, search: "", append: false });
  }, [fetchBatchPage, resetSelectAllBatchState]);

  // ── CLASSROOM PAGINATION ───────────────────────────────────────────────────
  const fetchClassroomPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (classroomLoadingRef.current) return [];
      const seq = (classroomSeq.current += 1);
      classroomLoadingRef.current = true;
      setClassroomLoading(true);
      try {
        const resp = await getClassrooms(PAGE_SIZE, page, search, false);
        if (seq !== classroomSeq.current) return [];
        const list = (resp?.data || []).map((c) => ({
          value: c.id,
          label: c.classroomNo,
          classroomNo: c.classroomNo,
        }));
        const hasNext = Boolean(resp?.pagination?.hasNext);

        if (append) {
          setClassroomOptions((prev) => mergePaginatedOptions(prev, list));
        } else {
          // Preserve pre-seeded classroom option from edit load
          setClassroomOptions((prev) => {
            const preSeeded = prev.filter((o) => o._preSeeded);
            return mergePaginatedOptions(preSeeded, list);
          });
        }

        classroomPageRef.current = page;
        classroomSearchRef.current = search;
        classroomHasMoreRef.current = hasNext;
        return list;
      } catch (err) {
        console.error("Error loading classrooms:", err);
        if (!append) setClassroomOptions([]);
        classroomHasMoreRef.current = false;
        return [];
      } finally {
        if (seq === classroomSeq.current) {
          classroomLoadingRef.current = false;
          setClassroomLoading(false);
        }
      }
    },
    [],
  );

  const fetchClassroomSearchPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (classroomLoadingRef.current) return [];
      const seq = (classroomSeq.current += 1);
      classroomLoadingRef.current = true;
      setClassroomSearchLoading(true);
      try {
        const resp = await getClassrooms(PAGE_SIZE, page, search, false);
        if (seq !== classroomSeq.current) return [];
        const list = (resp?.data || []).map((c) => ({
          value: c.id,
          label: c.classroomNo,
          classroomNo: c.classroomNo,
        }));
        const hasNext = Boolean(resp?.pagination?.hasNext);

        if (append) {
          setClassroomOptions((prev) => mergePaginatedOptions(prev, list));
        } else {
          setClassroomOptions((prev) => {
            const preSeeded = prev.filter((o) => o._preSeeded);
            return mergePaginatedOptions(preSeeded, list);
          });
        }

        classroomSearchPageRef.current = page;
        classroomSearchHasMoreRef.current = hasNext;
        return list;
      } catch (err) {
        console.error("Error loading search classrooms:", err);
        if (!append) setClassroomOptions([]);
        return [];
      } finally {
        if (seq === classroomSeq.current) {
          classroomLoadingRef.current = false;
          setClassroomSearchLoading(false);
        }
      }
    },
    [],
  );

  const loadClassroomOptions = useCallback(
    async (inputValue, page = 1) => {
      if (inputValue) {
        classroomCurrentSearchRef.current = inputValue;
        classroomSearchPageRef.current = page;
        return await fetchClassroomSearchPage({
          page: page,
          search: String(inputValue || ""),
          append: page > 1,
        });
      } else {
        classroomPageRef.current = page;
        return await fetchClassroomPage({
          page: page,
          search: "",
          append: page > 1,
        });
      }
    },
    [fetchClassroomPage, fetchClassroomSearchPage],
  );

  const handleClassroomMenuOpen = useCallback(async () => {
    if (classroomLoadingRef.current) return;
    await fetchClassroomPage({ page: 1, search: "", append: false });
  }, [fetchClassroomPage]);

  const handleClassroomScrollToBottom = useCallback(async () => {
    if (classroomLoadingRef.current || !classroomHasMoreRef.current) return;
    await fetchClassroomPage({
      page: classroomPageRef.current + 1,
      search: classroomSearchRef.current,
      append: true,
    });
  }, [fetchClassroomPage]);

  const handleClassroomSearchScrollToBottom = useCallback(async (searchTerm) => {
    if (classroomLoadingRef.current || !classroomSearchHasMoreRef.current) return;
    await fetchClassroomSearchPage({
      page: classroomSearchPageRef.current + 1,
      search: searchTerm,
      append: true,
    });
  }, [fetchClassroomSearchPage]);

  const refreshClassrooms = useCallback(async () => {
    classroomSeq.current += 1;
    classroomSearchRef.current = "";
    classroomPageRef.current = 1;
    classroomHasMoreRef.current = true;
    classroomSearchPageRef.current = 1;
    classroomSearchHasMoreRef.current = true;
    classroomCurrentSearchRef.current = "";
    await fetchClassroomPage({ page: 1, search: "", append: false });
  }, [fetchClassroomPage]);

  // ── INIT ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    try {
      setLoading(true);
      const userProfile = await getMe();
      const userData = userProfile.data;
      const userId = getCurrentUserIdFromData(userData);
      if (!userId) {
        toast.error("Unable to fetch user information");
        return;
      }

      setCurrentUser(userData);
      // Check every profile, not just profiles[0] -- a user can hold more
      // than one role (e.g. Trainer plus a separately-granted scoped role),
      // and relying on index 0 wrongly showed the full "pick any trainer"
      // selector to trainers whose Trainer profile wasn't first in the list.
      const profileLabels = (userData.profiles || []).map((p) => p?.label?.toLowerCase()).filter(Boolean);
      const isTrainerUser = profileLabels.includes("trainer");
      const isAdminUser = userData.isSuperAdmin || profileLabels.includes("admin") || profileLabels.includes("superadmin") || profileLabels.includes("crm");
      setIsTrainer(isTrainerUser);
      if (isTrainerUser) setCurrentTrainerId(userId);

      if (id) {
        await fetchEventDetails(id, isTrainerUser, isAdminUser, userId, userData);
      } else {
        const defaultTrainerName =
          isTrainerUser && userData
            ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim()
            : "";
        setEventData([
          {
            name: "", date: "", time: "09:00", venueId: "",
            onlineLink: "", offlineAddress: "", organizerName: "",
            organizerEmail: "", organizerPhone: "", description: "",
            agenda: "", highlights: "", category: "",
            priority: PRIORITY_NOT_SET, classroomNumber: "",
            trainerIds: isTrainerUser ? [userId] : [],
            trainerName: defaultTrainerName,
            batchIds: [], startDateTime: "", endDateTime: "",
          },
        ]);
        setIsEditing(false);
      }
      setHasInitialized(true);
    } catch (err) {
      console.error("Failed to initialize component:", err);
      toast.error("Failed to initialize component");
    } finally {
      setLoading(false);
    }
  };

  const fetchEventDetails = async (
    eventId, isTrainerUser, isAdminUser, userId, userData,
  ) => {
    try {
      const res = await getEventById(eventId);
      const selectedEvent = res?.data;
      if (!selectedEvent) {
        const defaultTrainerName =
          isTrainerUser && userData
            ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim()
            : "";
        setEventData([
          {
            name: "", date: "", time: "09:00", venueId: "",
            onlineLink: "", offlineAddress: "", organizerName: "",
            organizerEmail: "", organizerPhone: "", description: "",
            agenda: "", highlights: "", category: "",
            priority: PRIORITY_NOT_SET, classroomNumber: "",
            trainerIds: isTrainerUser && userId ? [userId] : [],
            trainerName: defaultTrainerName,
            batchIds: [], startDateTime: "", endDateTime: "",
          },
        ]);
        setIsEditing(false);
        return;
      }

      let dateString = "";
      let timeString = "09:00";
      if (selectedEvent.date) {
        dateString = new Date(selectedEvent.date).toISOString().split("T")[0];
      }
      if (selectedEvent.time) {
        if (selectedEvent.time.includes("T") || selectedEvent.time.includes("Z")) {
          timeString = moment.utc(selectedEvent.time).format("HH:mm");
        } else if (selectedEvent.time.includes(":")) {
          timeString = selectedEvent.time.slice(0, 5);
        }
      }

      const trainerIdsArray = selectedEvent.trainerId
        ? Array.isArray(selectedEvent.trainerId)
          ? selectedEvent.trainerId.map((t) => t?.id).filter(Boolean)
          : selectedEvent.trainerId.split(",").map((t) => t.trim())
        : [];

      const batchIdsArray = selectedEvent.batchId
        ? Array.isArray(selectedEvent.batchId)
          ? selectedEvent.batchId.map((b) => b?.id).filter(Boolean)
          : selectedEvent.batchId.split(",").map((b) => b.trim())
        : [];

      // ── Pre-seed trainer options with _preSeeded flag ──────────────────────
      if (Array.isArray(selectedEvent.trainerId)) {
        const trainerOpts = selectedEvent.trainerId
          .filter((t) => t && t.id)
          .map((t) => ({
            value: t.id,
            label: t.trainerName || `Trainer ${(t.id || "").slice(0, 6)}`,
            specialization: t.specialization || "",
            email: t.trainerEmail || "",
            _preSeeded: true,
          }));
        if (trainerOpts.length) setTrainerOptions(trainerOpts);
      }

      // ── Pre-seed batch options with _preSeeded flag ────────────────────────
      if (Array.isArray(selectedEvent.batchId)) {
        const batchOpts = selectedEvent.batchId
          .filter((b) => b && b.id)
          .map((b) => ({
            value: String(b.id),
            label: b.name || "Unknown",
            trainerId: b.trainerId || null,
            _preSeeded: true,
          }));
        if (batchOpts.length) {
          setFilteredBatches(batchOpts);
          batchOpts.forEach((o) =>
            batchLabelCacheRef.current.set(
              String(o.value),
              String(o.label || o.value),
            ),
          );
        }
      }

      // ── Pre-seed classroom option with _preSeeded flag ─────────────────────
      let venueId = "";
      let classroomNumber = "";

      if (
        Array.isArray(selectedEvent.venueId) &&
        selectedEvent.venueId.length > 0
      ) {
        const venue = selectedEvent.venueId[0];
        venueId = venue.id || "";
        classroomNumber = venue.classroomNo || "";

        if (venueId) {
          const preSeededClassroom = {
            value: venueId,
            label: classroomNumber,
            classroomNo: classroomNumber,
            _preSeeded: true,
          };
          // Store in ref — always accessible synchronously in render
          preSeededClassroomRef.current = preSeededClassroom;
          // Also seed classroomOptions state so dropdown list has it
          setClassroomOptions([preSeededClassroom]);
        }
      } else if (
        selectedEvent.venueId &&
        typeof selectedEvent.venueId === "string"
      ) {
        venueId = selectedEvent.venueId;
      }

      const eventDataObj = {
        ...selectedEvent,
        date: dateString,
        time: timeString,
        trainerIds: trainerIdsArray.map((x) => String(x).trim()),
        batchIds: batchIdsArray.map((x) => String(x).trim()),
        venueId: venueId,
        classroomNumber: classroomNumber,
        startDateTime: selectedEvent.startDateTime || "",
        endDateTime: selectedEvent.endDateTime || "",
        category: selectedEvent.category || "",
        // Events created before priorities existed come back without one.
        priority: normalizePriority(selectedEvent.priority),
      };

      if (selectedEvent.category === "Holiday") eventDataObj.name = "Holiday";

      setEventData([eventDataObj]);
      setIsEditing(true);
      // Bump formKey AFTER all state is set — this remounts Formik with
      // correct initialValues so category + venueId are pre-populated.
      setFormKey(`edit-${eventId}`);
    } catch (error) {
      console.error("Error fetching event details:", error);
      toast.error("Error fetching event details!");
    }
  };

  // Sync trainerIdsRef on edit / trainer-user init
  useEffect(() => {
    if (!hasInitialized) return;
    if (isEditing && eventData?.[0]?.trainerIds?.length > 0) {
      trainerIdsRef.current = eventData[0].trainerIds.map((x) => String(x));
      setTrainersWithoutBatches([]);
    } else if (isTrainer && !isEditing && currentTrainerId) {
      trainerIdsRef.current = [String(currentTrainerId)];
      setTrainersWithoutBatches([]);
    }
  }, [hasInitialized, isEditing, isTrainer, currentTrainerId]);

  // ── Form config ────────────────────────────────────────────────────────────
  const categoryOptions = [
    { value: "Event", label: "Event" },
    { value: "Mock Interview", label: "Mock Interview" },
    { value: "Holiday", label: "Holiday" },
  ];

  const validationSchema = Yup.object().shape({
    events: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.notRequired(),
          otherwise: (s) => s.required("Event Name is required"),
        }),
        startDateTime: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.required("This is the required field"),
          otherwise: (s) => s.notRequired(),
        }),
        endDateTime: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.required("This is the required field"),
          otherwise: (s) => s.notRequired(),
        }),
        date: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.notRequired(),
          otherwise: (s) => s.required("Date is required"),
        }),
        time: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required("Start Time is required"),
        }),
        venueId: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required("Classroom is required"),
        }),
        onlineLink: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.test("holiday-link", "", () => true),
          otherwise: (s) => s.url("Invalid URL").nullable().notRequired(),
        }),
        offlineAddress: Yup.string().nullable().notRequired(),
        organizerName: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required("Organizer Name is required"),
        }),
        organizerEmail: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.test("holiday-email", "", () => true),
          otherwise: (s) =>
            s.email("Invalid email").required("Organizer Email is required"),
        }),
        organizerPhone: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required("Organizer Phone is required"),
        }),
        description: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required("Description is required"),
        }),
        agenda: Yup.string().nullable().notRequired(),
        highlights: Yup.string().nullable().notRequired(),
        category: Yup.string().required("Category is required"),
        priority: Yup.string()
          .oneOf(priorityOptions.map((o) => o.value))
          .required("Priority is required"),
        classroomNumber: Yup.string().nullable().notRequired(),
        trainerIds: Yup.array()
          .min(1, "At least one Trainer is required")
          .required("Trainer is required"),
        batchIds: Yup.array()
          .min(1, "At least one Batch is required")
          .required("Batch is required"),
      }),
    ),
  });

  const getInitialValues = () => {
    const userId = getCurrentUserId();
    if (isEditing && eventData?.length > 0) {
      return {
        events: [
          {
            ...eventData[0],
            trainerIds: eventData[0].trainerIds || [],
            batchIds: eventData[0].batchIds || [],
            // Ensure category is a plain string matching categoryOptions values
            category: eventData[0].category || "",
            priority: normalizePriority(eventData[0].priority),
            // Ensure venueId is a plain string ID
            venueId: eventData[0].venueId || "",
          },
        ],
      };
    }
    return {
      events: [
        {
          name: "", date: "", time: "09:00", venueId: "",
          onlineLink: "", offlineAddress: "", organizerName: "",
          organizerEmail: "", organizerPhone: "", description: "",
          agenda: "", highlights: "", category: "",
          priority: PRIORITY_NOT_SET, classroomNumber: "",
          trainerIds: isTrainer && userId ? [String(userId)] : [],
          trainerName:
            isTrainer && currentUser
              ? `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim()
              : "",
          batchIds: [], startDateTime: "", endDateTime: "",
        },
      ],
    };
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleCategoryChange = (selectedOption, setFieldValue) => {
    if (isEditing) return; // Prevent category change on edit
    const category = selectedOption?.value || "";
    setFieldValue("events[0].category", category);
    if (category === "Holiday") {
      [
        "name", "date", "time", "venueId", "onlineLink", "offlineAddress",
        "organizerName", "organizerEmail", "organizerPhone",
        "description", "agenda", "highlights",
      ].forEach((f) => setFieldValue(`events[0].${f}`, ""));
      setFieldValue("events[0].startDateTime", new Date().toISOString());
      setFieldValue(
        "events[0].endDateTime",
        new Date(Date.now() + 86400000).toISOString(),
      );
    } else {
      setFieldValue("events[0].startDateTime", "");
      setFieldValue("events[0].endDateTime", "");
      setFieldValue("events[0].name", "");
      setFieldValue("events[0].venueId", "");
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (!values.events?.length) {
        toast.error("No events to submit!");
        setSubmitting(false);
        return;
      }
      const event = values.events[0];

      // A new event cannot be set for a time that has gone; an existing one
      // stays editable. POST /events refuses it too.
      if (!isEditing && event.category !== "Holiday" && isPastSchedule(event.date, event.time)) {
        toast.error(PAST_SCHEDULE_MESSAGE);
        setSubmitting(false);
        return;
      }

      const payload = {
        name: event.name,
        date: event.date,
        time: event.time,
        venueId: event.venueId || null,
        onlineLink: event.onlineLink?.trim() || null,
        offlineAddress: event.offlineAddress?.trim() || null,
        organizerName: event.organizerName || null,
        organizerEmail: event.organizerEmail || null,
        organizerPhone: event.organizerPhone || null,
        description: event.description || null,
        agenda: event.agenda || null,
        highlights: event.highlights?.trim() || null,
        category: event.category,
        priority: normalizePriority(event.priority),
        classroomNumber: event.classroomNumber?.trim() || null,
        startDateTime: event.startDateTime || null,
        endDateTime: event.endDateTime || null,
      };

      payload.trainerId =
        event.trainerIds?.length > 0 ? event.trainerIds.join(",") : "";
      payload.batchId =
        event.batchIds?.length > 0 ? event.batchIds.join(",") : "";

      if (payload.category === "Holiday") {
        payload.name = "Holiday";
        payload.time = DEFAULT_ISO;
        payload.date = DEFAULT_ISO;
        payload.startDateTime = event.startDateTime || new Date().toISOString();
        payload.endDateTime =
          event.endDateTime || new Date(Date.now() + 86400000).toISOString();
        payload.organizerName = "N/A";
        payload.organizerEmail = "N/A";
        payload.organizerPhone = "N/A";
        payload.description = "N/A";
        payload.agenda = "N/A";
        payload.highlights = "N/A";
        payload.venueId = "N/A";
        payload.onlineLink = "N/A";
        payload.offlineAddress = "N/A";
        payload.classroomNumber = "N/A";
      } else {
        // Read forgivingly -- a typed "29/09/2026" or "10:30 AM" used to
        // throw "Invalid time value" here and fail the whole submit.
        const when = toEventDateTime(event.date, event.time);
        if (!when) {
          toast.error(INVALID_SCHEDULE_MESSAGE);
          setSubmitting(false);
          return;
        }
        payload.date = when.date;
        payload.time = when.time;
        payload.startDateTime = DEFAULT_ISO;
        payload.endDateTime = DEFAULT_ISO;
      }

      if (isEditing) {
        await updateEvent(id, payload);
      } else {
        await createEvent(payload);
      }

      toast.success(`Event ${isEditing ? "updated" : "created"} successfully!`);
      resetForm();
      setEventData(null);
      setIsEditing(false);
      setFilteredBatches([]);
      resetSelectAllBatchState();
      setTimeout(() => navigate("/list-event-Form"), 500);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message?.message ||
        error.response?.data?.message ||
        error.response?.statusText ||
        "Submission failed";
      console.error("Submission error:", error);
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Trainer/Batch change handlers ──────────────────────────────────────────
  const onTrainersChange = (
    selectedOptions,
    setFieldValue,
    index,
    currentBatchIds,
  ) => {
    if (isTrainer && !isEditing) {
      toast.warning("As a trainer, you cannot change the trainer selection");
      return;
    }

    const selectedTrainerIds = selectedOptions
      ? selectedOptions.map((o) => o.value)
      : [];

    setFieldValue(`events[${index}].trainerIds`, selectedTrainerIds);
    setFieldValue(
      `events[${index}].trainerName`,
      selectedOptions ? selectedOptions.map((o) => o.label).join(", ") : "",
    );

    allTrainersLoadedRef.current = false;
    trainerIdsRef.current = selectedTrainerIds.map((x) => String(x));

    setFilteredBatches([]);
    setTrainersWithoutBatches([]);
    batchPageRef.current = 1;
    batchHasMoreRef.current = true;
    batchSearchRef.current = "";
    batchSearchPageRef.current = 1;
    batchSearchHasMoreRef.current = true;
    batchCurrentSearchRef.current = "";
    resetSelectAllBatchState();
    setFieldValue(`events[${index}].batchIds`, []);
  };

  const onBatchesChange = (selectedOptions, setFieldValue, index) => {
    (selectedOptions || []).forEach((o) =>
      batchLabelCacheRef.current.set(
        String(o.value),
        String(o.label || o.value),
      ),
    );
    setFieldValue(
      `events[${index}].batchIds`,
      selectedOptions ? selectedOptions.map((o) => o.value) : [],
    );
    if (selectAllBatchDone) {
      resetSelectAllBatchState();
    }
  };

  // ── Select-All Trainers ────────────────────────────────────────────────────
  const handleSelectAllTrainers = async (isChecked, setFieldValue, index) => {
    if (!isChecked) {
      setFieldValue(`events[${index}].trainerIds`, []);
      setFieldValue(`events[${index}].trainerName`, "");
      trainerIdsRef.current = [];
      allTrainersLoadedRef.current = false;
      setFilteredBatches([]);
      setTrainersWithoutBatches([]);
      batchPageRef.current = 1;
      batchHasMoreRef.current = true;
      batchSearchRef.current = "";
      resetSelectAllBatchState();
      setFieldValue(`events[${index}].batchIds`, []);
      return;
    }

    try {
      setTrainerLoading(true);
      trainerLoadingRef.current = true;
      const resp = await getTrainers(SELECT_ALL_TRAINER_SIZE, 1, "", false);
      const list = Array.isArray(resp?.data) ? resp.data : [];

      const allValues = [];
      const allLabels = [];
      const allOptions = [];

      list.forEach((t) => {
        const tid = String(t.id || "").trim();
        const label = t.trainerName || `Trainer ${tid.slice(0, 6)}`;
        if (tid) {
          allValues.push(tid);
          allLabels.push(label);
          allOptions.push({
            label,
            value: tid,
            specialization: t.specialization,
            email: t.traineremail || "",
          });
        }
      });

      setTrainerOptions(allOptions);
      allTrainersLoadedRef.current = true;
      setFieldValue(`events[${index}].trainerIds`, allValues);
      setFieldValue(`events[${index}].trainerName`, allLabels.join(", "));
      trainerIdsRef.current = allValues;

      setFilteredBatches([]);
      setTrainersWithoutBatches([]);
      batchPageRef.current = 1;
      batchHasMoreRef.current = true;
      batchSearchRef.current = "";
      resetSelectAllBatchState();
      setFieldValue(`events[${index}].batchIds`, []);
    } catch (err) {
      toast.error("Failed to select all trainers");
    } finally {
      trainerLoadingRef.current = false;
      setTrainerLoading(false);
    }
  };

  // ── Select-All Batches ─────────────────────────────────────────────────────
  const handleSelectAllBatches = async (isChecked, setFieldValue, index) => {
    if (!isChecked) {
      setFieldValue(`events[${index}].batchIds`, []);
      resetSelectAllBatchState();
      return;
    }

    const trainerIds = Array.isArray(trainerIdsRef.current)
      ? trainerIdsRef.current.map((x) => String(x).trim()).filter(Boolean)
      : [];
    if (!trainerIds.length) return;

    try {
      setSelectAllBatchLoading(true);
      selectAllBatchLoadingRef.current = true;

      const countResp = await getBatchesByTrainers(trainerIds, 1, 1, "", "name");
      const pagination = extractPagination(countResp);
      const totalCount = pagination?.totalCount ?? 0;

      if (totalCount === 0) {
        toast.info("No batches found for the selected trainer(s).");
        return;
      }

      const allResp = await getBatchesByTrainers(
        trainerIds,
        totalCount,
        1,
        "",
        "name",
      );
      const rawList = extractBatchList(allResp);

      const allOptions = [];
      const allValues = [];

      rawList.forEach((b) => {
        const bid = String(b?.id || b?._id || "").trim();
        const label = b?.name || bid;
        if (bid) {
          allValues.push(bid);
          batchLabelCacheRef.current.set(bid, label);
          allOptions.push({ value: bid, label, trainerId: b.trainerId || null });
        }
      });

      setFilteredBatches((prev) => mergePaginatedOptions(prev, allOptions));
      setFieldValue(`events[${index}].batchIds`, allValues);
      setSelectAllBatchDone(true);
      setSelectAllBatchTotal(allValues.length);
    } catch (err) {
      console.error("Failed to select all batches:", err);
      toast.error("Failed to select all batches");
    } finally {
      setSelectAllBatchLoading(false);
      selectAllBatchLoadingRef.current = false;
    }
  };

  // ── Sub-components ─────────────────────────────────────────────────────────
  const CustomSelectHeader = ({
    options,
    selectedValues,
    onSelectAll,
    label,
    disabled,
  }) => {
    const isAllSelected =
      options.length > 0 && selectedValues.length >= options.length;
    const isIndeterminate = selectedValues.length > 0 && !isAllSelected;
    /* `p-2 border-bottom d-flex justify-content-between align-items-center`
       was five utilities that resolve to nothing here, so the label and the
       checkbox stacked with no padding and no rule under them. */
    return (
      <div className="ef-selectall">
        <span className="ef-selectall-label">{label}</span>
        <FormCheck
          type="checkbox"
          label="Select all"
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={(e) => onSelectAll(e.target.checked)}
          disabled={disabled}
        />
      </div>
    );
  };

  // ── Render guard ───────────────────────────────────────────────────────────
  /* Nothing, while the record loads. This drew its own overlay; the app
     has one now, raised by the request itself (Loader/busy.js), so an
     empty page shell underneath it would only be a box with nothing in
     it waiting to be covered. */
  if (!eventData || loading) return null;

  // ── Render ─────────────────────────────────────────────────────────────────
  /* `.pg` is the house page shell -- a column with a 16px gap and the
     display face on its headings. The page used to be
     `dashboard-wrapper > td-page`, and `.td-page` only sets padding: 0, so
     the header sat flush against the card below it. */
  return (
    <div className="dashboard-wrapper">
      <div className="pg ef-page">

      <Formik
        key={formKey}
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleSubmit: formikSubmit,
          isSubmitting,
          errors,
          touched,
          setFieldValue,
        }) => {
          const event = values.events[0];
          const isHoliday = event.category === "Holiday";

          // ── Derive the currently-selected category option ────────────────
          const selectedCategoryOption =
            categoryOptions.find((o) => o.value === event.category) || null;

          // ── Derive the currently-selected classroom option ───────────────
          // Use ref as primary source (always sync) so the value shows
          // immediately on edit without waiting for classroomOptions state.
          const selectedClassroomOption =
            classroomOptions.find((o) => o.value === event.venueId) ||
            (preSeededClassroomRef.current?.value === event.venueId
              ? preSeededClassroomRef.current
              : null);

          return (
            <Form onSubmit={formikSubmit} className="ef-form">
              <header className="pg-head">
                <div className="pg-head-main">
                  <h1 className="pg-title">
                    {isEditing ? "Edit event" : "Add event"}
                  </h1>
                  <p className="pg-sub">
                    {isEditing
                      ? event.name
                        ? `Editing “${event.name}”. Category cannot be changed once an event exists.`
                        : "Category cannot be changed once an event exists."
                      : "Fill in the sections below. Batches are listed once you pick a trainer."}
                  </p>
                </div>
                <div className="pg-head-actions">
                  <Link to="/list-event-Form" className="pg-btn">
                    Go to event list
                  </Link>
                </div>
              </header>

              <div className="ef-card">
                <div className="ef-card-body">
                  <FieldArray name="events">
                    {() => (
                      <>
                        {values.events.map((event, index) => (
                          <div key={index}>

                            {/* ── Event details ─────────────────────────────
                                The form used to be one undivided card of
                                fifteen field rows. The sections are what make
                                it scannable; .ef-section rules them off. */}
                            <section className="ef-section">
                              <h2 className="ef-section-title">Event details</h2>

                              <Grid>
                                <Cell md={6}>
                                  <Form.Group>
                                    <Form.Label>
                                      Event category
                                      <span className="ef-req">*</span>
                                    </Form.Label>
                                    {/*
                                      FIX: Use selectedCategoryOption (derived from
                                      event.category string) as the value. This ensures
                                      the pre-populated category from the API is shown.
                                      key forces re-mount when category changes so the
                                      disabled select always reflects the current value.
                                    */}
                                    <UniqueAsyncSelect
                                      key={`category-${event.category}`}
                                      options={categoryOptions}
                                      value={selectedCategoryOption}
                                      onChange={(opt) =>
                                        handleCategoryChange(opt, setFieldValue)
                                      }
                                      placeholder="Select event category"
                                      isMulti={false}
                                      isDisabled={isEditing}
                                    />
                                    {errors.events?.[index]?.category &&
                                      touched.events?.[index]?.category && (
                                        <div className="text-danger">
                                          {errors.events[index].category}
                                        </div>
                                      )}
                                  </Form.Group>
                                </Cell>
                                {!isHoliday && (
                                  <Cell md={6}>
                                    <TextInput
                                      label="Event name"
                                      name={`events[${index}].name`}
                                      value={event.name}
                                      isRequired={true}
                                      onChange={handleChange}
                                      placeholder="Enter event name"
                                    />
                                  </Cell>
                                )}
                              </Grid>

                              {/* ── Priority ── */}
                              <Grid>
                                <Cell md={6}>
                                  <Form.Group>
                                    <Form.Label>
                                      Priority
                                      <span className="ef-req">*</span>
                                    </Form.Label>
                                    <UniqueAsyncSelect
                                      key={`priority-${event.priority}`}
                                      options={priorityOptions}
                                      value={
                                        priorityOptions.find(
                                          (o) => o.value === event.priority,
                                        ) || null
                                      }
                                      onChange={(opt) =>
                                        setFieldValue(
                                          `events[${index}].priority`,
                                          opt?.value || PRIORITY_NOT_SET,
                                        )
                                      }
                                      placeholder="Select priority"
                                      isMulti={false}
                                    />
                                    {/* The swatch was `d-inline-block
                                        rounded-circle` with its size in a style
                                        attribute -- neither class resolves, so
                                        an inline span ignored the width and
                                        height and no dot was drawn at all. */}
                                    <p className="ef-hint">
                                      <span
                                        className="ef-dot"
                                        style={{
                                          backgroundColor: getPriorityMeta(
                                            event.priority,
                                          ).color,
                                        }}
                                      />
                                      Students in the selected batches get this
                                      colour on the event notification.
                                    </p>
                                    {errors.events?.[index]?.priority &&
                                      touched.events?.[index]?.priority && (
                                        <div className="text-danger">
                                          {errors.events[index].priority}
                                        </div>
                                      )}
                                  </Form.Group>
                                </Cell>
                              </Grid>
                            </section>

                            {/* ── Schedule ── */}
                            <section className="ef-section">
                              <h2 className="ef-section-title">Schedule</h2>

                              <Grid>
                                {!isHoliday ? (
                                  <>
                                    <Cell md={6}>
                                      <Form.Group>
                                        <Form.Label>
                                          Date
                                          <span className="ef-req">*</span>
                                        </Form.Label>
                                        <DateTimePicker
                                          value={
                                            event.date ? new Date(event.date) : null
                                          }
                                          onChange={(date) => {
                                            if (date?._isAMomentObject)
                                              setFieldValue(
                                                `events[${index}].date`,
                                                date.format("YYYY-MM-DD"),
                                              );
                                            else if (date instanceof Date)
                                              setFieldValue(
                                                `events[${index}].date`,
                                                date.toISOString().split("T")[0],
                                              );
                                          }}
                                          dateFormat="YYYY-MM-DD"
                                          timeFormat={false}
                                          inputProps={{
                                            placeholder: "Select date",
                                            className: "form-control",
                                          }}
                                          closeOnSelect={true}
                                          isValidDate={(cur) =>
                                            // Today onward for a new event; an
                                            // existing one keeps whatever date it has.
                                            isEditing || !cur.isBefore(moment(), "day")
                                          }
                                        />
                                        {errors.events?.[index]?.date &&
                                          touched.events?.[index]?.date && (
                                            <div className="text-danger">
                                              {errors.events[index].date}
                                            </div>
                                          )}
                                      </Form.Group>
                                    </Cell>
                                    <Cell md={6}>
                                      <Form.Group>
                                        <Form.Label>
                                          Time
                                          <span className="ef-req">*</span>
                                        </Form.Label>
                                        <DateTimePicker
                                          value={
                                            event.time
                                              ? new Date(`1970-01-01T${event.time}:00`)
                                              : new Date("1970-01-01T09:00:00")
                                          }
                                          onChange={(date) => {
                                            if (date?._isAMomentObject)
                                              setFieldValue(
                                                `events[${index}].time`,
                                                date.format("HH:mm"),
                                              );
                                            else if (date instanceof Date)
                                              setFieldValue(
                                                `events[${index}].time`,
                                                date.toTimeString().slice(0, 5),
                                              );
                                          }}
                                          dateFormat={false}
                                          timeFormat="HH:mm"
                                          inputProps={{
                                            placeholder: "Select time",
                                            className: "form-control",
                                          }}
                                          closeOnSelect={true}
                                          timeConstraints={{ minutes: { step: 1 } }}
                                        />
                                        {errors.events?.[index]?.time &&
                                          touched.events?.[index]?.time && (
                                            <div className="text-danger">
                                              {errors.events[index].time}
                                            </div>
                                          )}
                                      </Form.Group>
                                    </Cell>
                                  </>
                                ) : (
                                  <>
                                    <Cell md={6}>
                                      <Form.Group>
                                        <Form.Label>
                                          Start date and time
                                          <span className="ef-req">*</span>
                                        </Form.Label>
                                        <DateTimePicker
                                          value={
                                            event.startDateTime
                                              ? new Date(event.startDateTime)
                                              : null
                                          }
                                          timeConstraints={{ minutes: { step: 1 } }}
                                          onChange={(date) => {
                                            if (date?._isAMomentObject)
                                              setFieldValue(
                                                `events[${index}].startDateTime`,
                                                date.toDate().toISOString(),
                                              );
                                            else if (date instanceof Date)
                                              setFieldValue(
                                                `events[${index}].startDateTime`,
                                                date.toISOString(),
                                              );
                                          }}
                                          dateFormat="YYYY-MM-DD"
                                          timeFormat="HH:mm"
                                          inputProps={{
                                            placeholder: "Select start date and time",
                                            className: "form-control",
                                          }}
                                          closeOnSelect={true}
                                        />
                                        {errors.events?.[index]?.startDateTime &&
                                          touched.events?.[index]?.startDateTime && (
                                            <div className="text-danger">
                                              {errors.events[index].startDateTime}
                                            </div>
                                          )}
                                      </Form.Group>
                                    </Cell>
                                    <Cell md={6}>
                                      <Form.Group>
                                        <Form.Label>
                                          End date and time
                                          <span className="ef-req">*</span>
                                        </Form.Label>
                                        <DateTimePicker
                                          value={
                                            event.endDateTime
                                              ? new Date(event.endDateTime)
                                              : null
                                          }
                                          timeConstraints={{ minutes: { step: 1 } }}
                                          onChange={(date) => {
                                            if (date?._isAMomentObject)
                                              setFieldValue(
                                                `events[${index}].endDateTime`,
                                                date.toDate().toISOString(),
                                              );
                                            else if (date instanceof Date)
                                              setFieldValue(
                                                `events[${index}].endDateTime`,
                                                date.toISOString(),
                                              );
                                          }}
                                          dateFormat="YYYY-MM-DD"
                                          timeFormat="HH:mm"
                                          inputProps={{
                                            placeholder: "Select end date and time",
                                            className: "form-control",
                                          }}
                                          closeOnSelect={true}
                                        />
                                        {errors.events?.[index]?.endDateTime &&
                                          touched.events?.[index]?.endDateTime && (
                                            <div className="text-danger">
                                              {errors.events[index].endDateTime}
                                            </div>
                                          )}
                                      </Form.Group>
                                    </Cell>
                                  </>
                                )}
                              </Grid>
                            </section>

                            {/* ── Who runs it ── */}
                            <section className="ef-section">
                              <h2 className="ef-section-title">Trainer and batches</h2>

                              <Grid>
                                <Cell md={12}>
                                  <Form.Group>
                                    <Form.Label>
                                      Trainer
                                      <span className="ef-req">*</span>
                                    </Form.Label>
                                    {isTrainer && !isEditing ? (
                                      <UniqueAsyncSelect
                                        isMulti={false}
                                        options={trainerOptions}
                                        isDisabled={true}
                                        value={{
                                          value: String(getCurrentUserId() || ""),
                                          label:
                                            event.trainerName ||
                                            `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() ||
                                            "You",
                                        }}
                                        onChange={() => {}}
                                        placeholder="Current Trainer"
                                        loadOptions={loadTrainerOptions}
                                        onMenuScrollToBottom={handleTrainerScrollToBottom}
                                        onSearchScrollToBottom={handleTrainerSearchScrollToBottom}
                                        isLoading={trainerLoading}
                                        isSearching={trainerSearchLoading}
                                        formatOptionLabel={(option) => (
                                          <span className="ef-opt">
                                            <span className="ef-opt-name">{option.label}</span>
                                            {option.specialization && (
                                              <span className="ef-opt-meta">
                                                {option.specialization}
                                              </span>
                                            )}
                                          </span>
                                        )}
                                      />
                                    ) : (
                                      /* .ef-inline, not `d-flex align-items-center
                                         gap-2` + `flex-grow-1`: none of those four
                                         resolve here, so the refresh button dropped
                                         below a full-width select. */
                                      <div className="ef-inline">
                                        <div className="ef-inline-grow">
                                            <CustomSelectHeader
                                              options={trainerOptions}
                                              selectedValues={event.trainerIds || []}
                                              onSelectAll={(isChecked) =>
                                                handleSelectAllTrainers(
                                                  isChecked,
                                                  setFieldValue,
                                                  index,
                                                )
                                              }
                                              label="Trainer(s)"
                                              disabled={trainerLoading}
                                            />
                                            <UniqueAsyncSelect
                                              isMulti
                                              options={trainerOptions}
                                              loadOptions={loadTrainerOptions}
                                              onMenuOpen={handleTrainerMenuOpen}
                                              onMenuScrollToBottom={handleTrainerScrollToBottom}
                                              onSearchScrollToBottom={handleTrainerSearchScrollToBottom}
                                              isLoading={trainerLoading}
                                              isSearching={trainerSearchLoading}
                                              value={trainerOptions.filter((o) =>
                                                event.trainerIds?.some(
                                                  (tid) =>
                                                    String(tid) === String(o.value),
                                                ),
                                              )}
                                              onChange={(opts) =>
                                                onTrainersChange(
                                                  opts,
                                                  setFieldValue,
                                                  index,
                                                  event.batchIds,
                                                )
                                              }
                                              placeholder="Select trainer(s)"
                                              formatOptionLabel={(option) => (
                                                <span className="ef-opt">
                                                  <span className="ef-opt-name">{option.label}</span>
                                                  {option.specialization && (
                                                    <span className="ef-opt-meta">
                                                      {option.specialization}
                                                    </span>
                                                  )}
                                                </span>
                                              )}
                                            />
                                          </div>
                                          <button
                                            type="button"
                                            className="pg-icon-btn"
                                            onClick={refreshTrainers}
                                            title="Refresh trainers"
                                            aria-label="Refresh trainers"
                                          >
                                            <RefreshCw size={15} />
                                          </button>
                                      </div>
                                    )}
                                    {errors.events?.[index]?.trainerIds &&
                                      touched.events?.[index]?.trainerIds && (
                                        <div className="text-danger">
                                          {errors.events[index].trainerIds}
                                        </div>
                                      )}
                                  </Form.Group>
                                </Cell>
                              </Grid>

                              {/* ── Batch ── */}
                              <Grid>
                                <Cell md={12}>
                                  <Form.Group>
                                    <Form.Label>
                                      Batch
                                      <span className="ef-req">*</span>
                                    </Form.Label>
                                    <div className="ef-inline">
                                      <div className="ef-inline-grow">
                                          <CustomSelectHeader
                                            options={filteredBatches}
                                            selectedValues={event.batchIds || []}
                                            onSelectAll={(isChecked) =>
                                              handleSelectAllBatches(
                                                isChecked,
                                                setFieldValue,
                                                index,
                                              )
                                            }
                                            label="Batch(es)"
                                            disabled={
                                              batchLoading ||
                                              selectAllBatchLoading
                                            }
                                          />
                                          <UniqueAsyncSelect
                                            key={`batch-${
                                              event.trainerIds
                                                ? event.trainerIds.join(",")
                                                : "empty"
                                            }`}
                                            isMulti
                                            options={filteredBatches}
                                            loadOptions={loadBatchOptions}
                                            onMenuOpen={handleBatchMenuOpen}
                                            onMenuScrollToBottom={handleBatchScrollToBottom}
                                            onSearchScrollToBottom={handleBatchSearchScrollToBottom}
                                            isLoading={batchLoading || selectAllBatchLoading}
                                            isSearching={batchSearchLoading}
                                            value={resolveBatchValue(event.batchIds)}
                                            onChange={(opts) =>
                                              onBatchesChange(opts, setFieldValue, index)
                                            }
                                            isDisabled={
                                              !event.trainerIds ||
                                              event.trainerIds.length === 0
                                            }
                                            placeholder={
                                              !event.trainerIds ||
                                              event.trainerIds.length === 0
                                                ? "Select trainer(s) to see batches"
                                                : selectAllBatchLoading
                                                  ? "Loading all batches…"
                                                  : batchLoading
                                                    ? "Loading batches..."
                                                    : filteredBatches.length === 0
                                                      ? "No batches found for selected trainer(s)"
                                                      : "Select batch(es)"
                                            }
                                          />

                                          {selectAllBatchDone &&
                                            selectAllBatchTotal > 0 && (
                                              <p className="ef-ok">
                                                ✓ All {selectAllBatchTotal} batches selected.
                                              </p>
                                            )}
                                        </div>

                                        <button
                                          type="button"
                                          className="pg-icon-btn"
                                          onClick={refreshBatches}
                                          title="Refresh batches"
                                          aria-label="Refresh batches"
                                        >
                                          <RefreshCw size={15} />
                                        </button>
                                    </div>
                                    {errors.events?.[index]?.batchIds &&
                                      touched.events?.[index]?.batchIds && (
                                        <div className="text-danger">
                                          {errors.events[index].batchIds}
                                        </div>
                                      )}
                                    {/* Was <Alert variant="warning">, which
                                        renders .alert.alert-warning -- neither
                                        exists here, so the note was unstyled
                                        body text. .pg-notice is the house one. */}
                                    {trainersWithoutBatches.length > 0 && (
                                      <p className="pg-notice ef-notice">
                                        <span>
                                          <strong>Note:</strong> these trainer(s) have no
                                          batches assigned: {trainersWithoutBatches.join(", ")}
                                        </span>
                                      </p>
                                    )}
                                  </Form.Group>
                                </Cell>
                              </Grid>

                            </section>

                            {/* ── Non-Holiday fields ── */}
                            {!isHoliday && (
                              <>
                                <section className="ef-section">
                                  <h2 className="ef-section-title">Where it happens</h2>

                                  <Grid>
                                    <Cell md={4}>
                                      <Form.Group>
                                        <Form.Label>
                                          Classroom
                                          <span className="ef-req">*</span>
                                        </Form.Label>
                                        <div className="ef-inline">
                                          <div className="ef-inline-grow">
                                            {/*
                                              FIX: Use selectedClassroomOption (derived
                                              from classroomOptions + event.venueId).
                                              key forces re-mount when venueId changes so
                                              the pre-seeded classroom always shows.
                                            */}
                                            <UniqueAsyncSelect
                                              key={`classroom-${event.venueId}`}
                                              options={classroomOptions}
                                              loadOptions={loadClassroomOptions}
                                              onMenuOpen={handleClassroomMenuOpen}
                                              onMenuScrollToBottom={handleClassroomScrollToBottom}
                                              onSearchScrollToBottom={handleClassroomSearchScrollToBottom}
                                              isLoading={classroomLoading}
                                              isSearching={classroomSearchLoading}
                                              value={selectedClassroomOption}
                                              onChange={(opt) => {
                                                setFieldValue(
                                                  `events[${index}].venueId`,
                                                  opt?.value || "",
                                                );
                                                setFieldValue(
                                                  `events[${index}].classroomNumber`,
                                                  opt?.classroomNo || "",
                                                );
                                              }}
                                              placeholder="Select classroom"
                                              isMulti={false}
                                              isClearable
                                            />
                                          </div>
                                          <button
                                            type="button"
                                            className="pg-icon-btn"
                                            onClick={refreshClassrooms}
                                            title="Refresh classrooms"
                                            aria-label="Refresh classrooms"
                                          >
                                            <RefreshCw size={15} />
                                          </button>
                                        </div>
                                        {errors.events?.[index]?.venueId &&
                                          touched.events?.[index]?.venueId && (
                                            <div className="text-danger">
                                              {errors.events[index].venueId}
                                            </div>
                                          )}
                                      </Form.Group>
                                    </Cell>
                                    <Cell md={4}>
                                      <TextInput
                                        label="Online link"
                                        isRequired={true}
                                        name={`events[${index}].onlineLink`}
                                        value={event.onlineLink || ""}
                                        onChange={handleChange}
                                      />
                                      {errors.events?.[index]?.onlineLink &&
                                        touched.events?.[index]?.onlineLink && (
                                          <div className="text-danger">
                                            {errors.events[index].onlineLink}
                                          </div>
                                        )}
                                    </Cell>
                                    <Cell md={4}>
                                      <TextInput
                                        label="Offline address"
                                        isRequired={true}
                                        name={`events[${index}].offlineAddress`}
                                        value={event.offlineAddress || ""}
                                        onChange={handleChange}
                                      />
                                    </Cell>
                                  </Grid>
                                </section>

                                <section className="ef-section">
                                  <h2 className="ef-section-title">Organizer</h2>

                                  <Grid>
                                    <Cell md={4}>
                                      <TextInput
                                        label="Organizer name"
                                        isRequired={true}
                                        name={`events[${index}].organizerName`}
                                        value={event.organizerName}
                                        onChange={handleChange}
                                      />
                                    </Cell>
                                    <Cell md={4}>
                                      <TextInput
                                        label="Organizer email"
                                        name={`events[${index}].organizerEmail`}
                                        type="email"
                                        isRequired={true}
                                        value={event.organizerEmail}
                                        onChange={handleChange}
                                      />
                                    </Cell>
                                    <Cell md={4}>
                                      <TextInput
                                        label="Organizer phone"
                                        name={`events[${index}].organizerPhone`}
                                        value={event.organizerPhone}
                                        isRequired={true}
                                        onChange={handleChange}
                                      />
                                    </Cell>
                                  </Grid>
                                </section>

                                <section className="ef-section">
                                  <h2 className="ef-section-title">Description and agenda</h2>

                                  <Grid>
                                    <Cell md={12}>
                                      <Form.Group>
                                        <Form.Label>
                                          Description
                                          <span className="ef-req">*</span>
                                        </Form.Label>
                                        <Form.Control
                                          as="textarea"
                                          rows={5}
                                          value={event.description || ""}
                                          onChange={(e) =>
                                            setFieldValue(
                                              `events[${index}].description`,
                                              e.target.value,
                                            )
                                          }
                                        />
                                        {errors.events?.[index]?.description &&
                                          touched.events?.[index]?.description && (
                                            <div className="text-danger">
                                              {errors.events[index].description}
                                            </div>
                                          )}
                                      </Form.Group>
                                    </Cell>
                                  </Grid>

                                  <Grid>
                                    <Cell md={12}>
                                      <TextAreaInput
                                        rows={4}
                                        isRequired={false}
                                        label="Agenda"
                                        name={`events[${index}].agenda`}
                                        value={event.agenda}
                                        onChange={handleChange}
                                      />
                                    </Cell>
                                  </Grid>

                                  <Grid>
                                    <Cell md={12}>
                                      <TextAreaInput
                                        rows={3}
                                        label="Highlights"
                                        isRequired={false}
                                        name={`events[${index}].highlights`}
                                        value={event.highlights || ""}
                                        onChange={handleChange}
                                      />
                                    </Cell>
                                  </Grid>
                                </section>
                              </>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </FieldArray>

                  {/* Was two half-width Bootstrap columns, so the two
                      buttons sat as one full-width bar each. A footer
                      row instead, actions to the right. */}
                  <div className="ef-foot">
                    <Link to="/list-event-Form" className="pg-btn">
                      Cancel
                    </Link>
                    <button
                      type="submit"
                      className="pg-btn pg-btn--main"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Processing..."
                        : isEditing
                          ? "Update event"
                          : "Submit event"}
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
      </div>
    </div>
  );
}

export default AddEvent;